<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      :active-step="pageTitle"
    />
    <PageHeading
      id="trustPendingRecordPageHeading"
      :default-heading="pageTitle"
      :edit-heading="pageTitle"
    />
    <div class="welcome-back__container">
      <div
        v-if="recordStatus === 'hasUnsubmittedRecord'"
        class="pvd-container container-divider"
      >
        <div class="primary-message">
          <h2
            id="trustPendingRecordInfo"
            class="pvd-heading-L"
          >
            You have a saved application for a trust account.
          </h2>
        </div>
        <div class="button-container">
          <button
            class="pvd-btn btn-action large"
            :disabled="inProgress"
            @click.prevent="continueSavedApplication()"
          >
            <pvd-loading-spinner
              v-if="continueInProgress"
              :active="continueInProgress"
              type="white"
              inline="true"
            />
            Continue saved application
          </button>
        </div>
        <div class="secondary-message">
          If you need to start a new application it will cancel your saved application.
        </div>
        <div class="button-container">
          <button
            class="pvd-btn btn-ghost-action"
            :disabled="inProgress"
            @click.prevent="showStartNewModal()"
          >
            <pvd-loading-spinner
              v-if="startNewInProgress"
              :active="startNewInProgress"
              type="brand"
              inline="true"
            />
            Start new application
          </button>
        </div>
      </div>
      <div
        v-if="recordStatus === 'hasCompletedRecord' && !isRep"
        class="pvd-container container-divider"
      >
        <div class="primary-message">
          <h2
            id="trustPendingRecordInfo"
            class="pvd-heading-L"
          >
            You have one more step...
          </h2>
        </div>
        <div class="progress-bar">
          <apx-graphical-progress>
            <apx-graphical-progress-item
              status="complete"
              :graphic="require('../../public/icons/prefill-icon.svg')"
              text="You started the account opening process and added your signature."
            />
            <apx-graphical-progress-item
              status="complete"
              :graphic="require('../../public/icons/esign-icon.svg')"
              text="All required trustees have signed."
            />
            <apx-graphical-progress-item
              :graphic="require('../../public/icons/tablet-icon.svg')"
              text="Continue to finish opening the account."
            />
          </apx-graphical-progress>
        </div>
        <div class="button-container">
          <button
            class="pvd-btn btn-action large"
            :disabled="inProgress"
            @click.prevent="finishOpeningAccount()"
          >
            <pvd-loading-spinner
              v-if="finishingInProgress"
              :active="finishingInProgress"
              type="white"
              inline="true"
            />
            Continue application
          </button>
        </div>
        <div class="secondary-message">
          If you need to start a new application, it will cancel your saved application.
        </div>
        <div class="button-container">
          <button
            class="pvd-btn btn-ghost-action"
            :disabled="inProgress"
            @click.prevent="showStartNewModal()"
          >
            <pvd-loading-spinner
              v-if="startNewInProgress"
              :active="startNewInProgress"
              type="brand"
              inline="true"
            />
            Start new application
          </button>
        </div>
      </div>
      <div
        v-if="recordStatus === 'hasCompletedRecord' && isRep"
        class="pvd-container container-divider"
      >
        <div class="primary-message">
          <h2
            id="trustPendingRecordInfo"
            class="pvd-heading-L"
          >
            There's one more step...
          </h2>
        </div>
        <div class="progress-bar">
          <apx-graphical-progress>
            <apx-graphical-progress-item
              status="complete"
              :graphic="require('../../public/icons/esign-icon.svg')"
              text="All required trustees have signed."
            />
            <apx-graphical-progress-item
              status="complete"
              :graphic="require('../../public/icons/email-icon.svg')"
              text="Primary trustee was notified by email or text to finish opening account."
            />
            <apx-graphical-progress-item
              :graphic="require('../../public/icons/tablet-icon.svg')"
              text="Have primary trustee log in to fidelity.com/greenline to finish opening account."
            />
          </apx-graphical-progress>
        </div>
        <div class="secondary-message">
          If you need to start a new application, it will cancel your saved application.
        </div>
        <div class="button-container">
          <button
            class="pvd-btn btn-ghost-action"
            :disabled="inProgress"
            @click.prevent="showStartNewModal()"
          >
            <pvd-loading-spinner
              v-if="startNewInProgress"
              :active="startNewInProgress"
              type="brand"
              inline="true"
            />
            Start new application
          </button>
        </div>
      </div>
      <pvd-modal
        id="start-new-application-modal"
        class="small text-left"
      >
        <div slot="modal-header">
          <h2 class="pvd-heading-L">
            Are you sure you want to start a new application?
          </h2>
        </div>
        <div slot="modal-body">
          <div class="pvd-container">
            <p>
              Starting a new trust application will delete the current one. If you'd like to stay with the current application,
              please wait until your account has been opened before starting a new one.
            </p>
          </div>
        </div>
        <div slot="modal-footer">
          <div class="pvd-btn-row">
            <div class="button-group">
              <button
                class="pvd-btn btn-action"
                style="display:inline; white-space:nowrap"
                :disabled="inProgress"
                nowrap
                @click.prevent="startNewButtonClicked()"
              >
                <pvd-loading-spinner
                  v-if="startNewInProgress"
                  :active="startNewInProgress"
                  type="white"
                  inline="true"
                />
                Start new application
              </button>
              <button
                class="pvd-btn btn-secondary"
                @click.prevent="exitStartNewModal()"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </pvd-modal>
      <PageNavigation
        :show-primary-button="'N'"
        :show-secondary-button="'N'"
        :page-exit-url="computedExitUrl"
      />
    </div>
  </div>
</template>

<script>
import PageNavigation from '../components/PageNavigation.vue';
import dataHelper from '../mixins/dataHelper';
import PageHeading from '../components/PageHeading.vue';
import ProgressBar from '../components/ProgressBar.vue';
import utility from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import measurement from '../mixins/measurement';
import pageNavigationHelper from '../mixins/pageNavigationHelper';

export default {
  name: 'TrustPendingRecord',
  components: {
    PageHeading,
    ProgressBar,
    PageNavigation,
  },
  mixins: [dataHelper, utility, validationHelper, measurement, pageNavigationHelper],
  inject: ['$validator'],
  data() {
    return {
      recordStatus: '',
      isPrimaryButtonLoading: false,
      isSecondaryButtonLoading: false,
      pageTitle: 'Welcome back',
      startNewModalActive: false,

      continueInProgress: false,
      startNewInProgress: false,
      finishingInProgress: false,
      inProgress: false,
    };
  },
  computed: {
    isRep() {
      return this.uc_isRep || this.isMaestro();
    },
  },
  mounted() {
    this.recordStatus = this.getInstanceData().trustPendingRecord.recordStatus;
    this.isPrimaryButtonLoading = false;
    this.isSecondaryButtonLoading = false;

    document.title = this.setTitle(this.pageTitle);
  },
  methods: {
    showStartNewModal() {
      document
        .querySelector('#start-new-application-modal')
        .setAttribute('active', 'true');
    },
    exitStartNewModal() {
      document
        .querySelector('#start-new-application-modal')
        .setAttribute('active', 'false');
    },
    startNewButtonClicked() {
      this.inProgress = true;
      this.startNewInProgress = true;
      this.setInstanceData('trustPendingRecord', { disable: true });
      this.performNavigation('startNewApplication');
    },
    continueSavedApplication() {
      this.inProgress = true;
      this.continueInProgress = true;
      this.setInstanceData('trustPendingRecord', { disable: true });
      this.performNavigation('continueSavedApplication');
    },
    finishOpeningAccount() {
      this.inProgress = true;
      this.finishingInProgress = true;
      this.setInstanceData('trustPendingRecord', { disable: true });
      this.performNavigation('finishOpeningAccount');
    },
  },
};
</script>

<style scoped lang="scss">

  .welcome-back__container {
    text-align: center;
  }

  .primary-message {
    // margin-bottom: 2em;
  }

  .secondary-message {
    margin-top: 3em;
  }

  .progress-bar {
    margin: 0 auto 0;
    max-width: 800px !important;
    padding: 30px 0;
  }

  .button-container {
    margin-top: 2em;
    text-align: center;
  }
</style>
